import React, { useState, useRef } from "react";
import { Search, Plus, ChevronDown, ChevronRight, Check, X } from "lucide-react";
import "./Chat.css";

const VISIBLE_LIMIT = 5;

const inlineDmInputStyle = { width: '100%', height: '32px', padding: '3px 26px', border: '1.5px solid #0DDFAB', borderRadius: '8px', fontSize: '12px', outline: 'none', background: '#fafafa', boxSizing: 'border-box' };
const inlineChannelInputStyle = { flex: 1, height: '32px', padding: '3px 10px', border: '1.5px solid #0DDFAB', borderRadius: '8px', fontSize: '12px', outline: 'none', background: '#fafafa', boxSizing: 'border-box' };

const getImgUrl = (img) => {
  if (!img || img === "null" || img === "undefined") return null;
  if (img.startsWith('http')) return img;
  const baseUrl = (process.env.REACT_APP_BASE_URL || "http://localhost:5026/api/v1").split("/api/v1")[0];
  if (img.includes('/')) {
    const norm = img.startsWith('/') ? img : `/${img}`;
    return `${baseUrl}${norm.startsWith('/public') ? norm : `/public${norm}`}`;
  }
  return (process.env.REACT_APP_USER_DOCS_PATH || `${baseUrl}/public/users/docs/`) + img;
};

const RoomAvatar = ({ imgSrc, initial, size = 28, shape = "square" }) => {
  const [resolvedUrl, setResolvedUrl] = useState(null);
  const url = getImgUrl(imgSrc);

  React.useEffect(() => {
    if (!url) { setResolvedUrl(null); return; }
    setResolvedUrl(null);
    const img = new Image();
    img.onload = () => setResolvedUrl(url);
    img.onerror = () => setResolvedUrl(null);
    img.src = url;
  }, [url]);

  return (
    <div style={{
      width: size, height: size, minWidth: size, borderRadius: shape === "square" ? 6 : "50%",
      background: "#0DDFAB", display: "flex", alignItems: "center", justifyContent: "center",
      overflow: "hidden", flexShrink: 0, color: "#fff", fontWeight: 600,
      fontSize: Math.max(10, Math.floor(size * 0.4))
    }}>
      {resolvedUrl ? (
        <img src={resolvedUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <span style={{ pointerEvents: "none", userSelect: "none" }}>{initial || "?"}</span>
      )}
    </div>
  );
};

const RoomList = ({
  rooms, activeRoom, onSelectRoom, onNewChat,
  onSearchUsers, onStartDirectChat, onCreateChannel,
  currentUserId
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [collapsedSections, setCollapsedSections] = useState({ direct: false, project: false, channel: false });
  const [expandedSections, setExpandedSections] = useState({ direct: false, channel: false });
  const [collapsedProjects, setCollapsedProjects] = useState({});

  // Inline input state
  const [inlineInputType, setInlineInputType] = useState(null); // 'dm' | 'channel' | 'subchannel'
  const [inlineInputParentId, setInlineInputParentId] = useState(null);
  const [inlineDmSearch, setInlineDmSearch] = useState("");
  const [inlineDmResults, setInlineDmResults] = useState([]);
  const [inlineChannelName, setInlineChannelName] = useState("");
  const searchTimerRef = useRef(null);

  const openInline = (type, parentId = null) => {
    setInlineInputType(type);
    setInlineInputParentId(parentId);
    setInlineDmSearch("");
    setInlineDmResults([]);
    setInlineChannelName("");
  };

  const closeInline = () => {
    setInlineInputType(null);
    setInlineInputParentId(null);
    setInlineDmSearch("");
    setInlineDmResults([]);
    setInlineChannelName("");
  };

  const handleDmSearchChange = (val) => {
    setInlineDmSearch(val);
    clearTimeout(searchTimerRef.current);
    if (val.length < 2) { setInlineDmResults([]); return; }
    searchTimerRef.current = setTimeout(async () => {
      if (onSearchUsers) {
        const results = await onSearchUsers(val);
        setInlineDmResults(results || []);
      }
    }, 300);
  };

  const handleStartDm = (userId) => {
    if (onStartDirectChat) onStartDirectChat(userId);
    closeInline();
  };

  const handleCreateChannelSubmit = () => {
    if (!inlineChannelName.trim()) return;
    if (onCreateChannel) onCreateChannel(inlineChannelName.trim(), inlineInputParentId);
    closeInline();
  };

  const toggleSection = (type) => setCollapsedSections(prev => ({ ...prev, [type]: !prev[type] }));
  const toggleExpand = (e, type) => { e.stopPropagation(); setExpandedSections(prev => ({ ...prev, [type]: !prev[type] })); };
  const toggleProject = (projectKey) => setCollapsedProjects(prev => ({ ...prev, [projectKey]: !prev[projectKey] }));

  const isDeletedMember = (m) => !m || m.is_deleted === true || m.status === 0 || m.status === "0";

  const getRoomName = (room) => {
    if (room.type === "direct" && room.members?.length > 0) {
      const otherMember = room.members.find(m => String(m.id || m._id) !== String(currentUserId));
      if (isDeletedMember(otherMember)) return "Deleted User";
      const name = `${otherMember.first_name || ""} ${otherMember.last_name || ""}`.trim();
      return name || "Deleted User";
    }
    return room.name || "Unnamed Space";
  };

  const filteredRooms = rooms.filter(room =>
    (getRoomName(room)).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderMoreRow = (type, remaining) => (
    <div
      onClick={(e) => toggleExpand(e, type)}
      className="sidebar-more-row"
      onMouseEnter={e => e.currentTarget.style.background = '#f1f3f4'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <span style={{ letterSpacing: 2, fontSize: 10 }}>•••</span>
      <span>More ({remaining})</span>
    </div>
  );

  const renderLessRow = (type) => (
    <div
      onClick={(e) => toggleExpand(e, type)}
      className="sidebar-more-row"
      onMouseEnter={e => e.currentTarget.style.background = '#f1f3f4'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <ChevronRight size={12} style={{ transform: 'rotate(270deg)' }} />
      <span>Show Less</span>
    </div>
  );

  // Inline DM search UI
  const renderInlineDmInput = () => (
    <div style={{ padding: '2px 12px 8px', position: 'relative' }}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <Search size={12} style={{ position: 'absolute', left: 8, color: '#aaa', pointerEvents: 'none', zIndex: 1 }} />
        <input
          autoFocus
          type="search"
          autoComplete="off"
          data-form-type="other"
          value={inlineDmSearch}
          onChange={e => handleDmSearchChange(e.target.value)}
          onKeyDown={e => { if (e.key === 'Escape') closeInline(); }}
          placeholder="Search users..."
          style={inlineDmInputStyle}
        />
        <X size={12} style={{ position: 'absolute', right: 8, cursor: 'pointer', color: '#aaa' }} onClick={closeInline} />
      </div>
      {inlineDmResults.length > 0 && (
        <div style={{ marginTop: 4, background: '#fff', border: '1px solid #f0f0f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          {inlineDmResults.slice(0, 6).map(u => (
            <div
              key={u.id}
              onClick={() => handleStartDm(u.id)}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', cursor: 'pointer', fontSize: '12px', fontWeight: '500', color: '#3c4043' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f1f3f4'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <RoomAvatar imgSrc={u.image} initial={u.first_name?.charAt(0)?.toUpperCase()} size={24} shape="round" />
              {u.first_name} {u.last_name}
            </div>
          ))}
        </div>
      )}
      {inlineDmSearch.length >= 2 && inlineDmResults.length === 0 && (
        <div style={{ marginTop: 4, textAlign: 'center', fontSize: '11px', color: '#aaa', padding: '4px 0' }}>No users found</div>
      )}
    </div>
  );

  // Inline channel name input UI
  const renderInlineChannelInput = () => (
    <div style={{ padding: '2px 12px 8px', display: 'flex', gap: '5px', alignItems: 'center' }}>
      <input
        autoFocus
        type="text"
        autoComplete="off"
        data-form-type="other"
        value={inlineChannelName}
        onChange={e => setInlineChannelName(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') handleCreateChannelSubmit();
          if (e.key === 'Escape') closeInline();
        }}
        placeholder="Channel name..."
        maxLength={30}
        style={inlineChannelInputStyle}
      />
      <div
        onClick={handleCreateChannelSubmit}
        title="Create"
        style={{ width: 26, height: 26, minWidth: 26, borderRadius: '6px', background: inlineChannelName.trim() ? '#0DDFAB' : '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: inlineChannelName.trim() ? 'pointer' : 'default', flexShrink: 0 }}
      >
        <Check size={13} color="#fff" strokeWidth={2.5} />
      </div>
      <div
        onClick={closeInline}
        title="Cancel"
        style={{ width: 26, height: 26, minWidth: 26, borderRadius: '6px', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
      >
        <X size={13} color="#888" />
      </div>
    </div>
  );

  // ── Direct Messages & standalone Channels ──────────────────────────────────
  const renderSection = (title, type, showAdd = false) => {
    const inlineKey = type === "direct" ? "dm" : "channel";
    const isShowingInline = inlineInputType === inlineKey && inlineInputParentId === null;
    const isCollapsed = collapsedSections[type];
    const isExpanded = expandedSections[type];
    const sectionRooms = filteredRooms.filter(r =>
      type === "channel" ? r.type === "channel" && r.parent_id == null : r.type === type
    );

    if (sectionRooms.length === 0 && !isShowingInline && !showAdd) return null;

    const visibleRooms = isExpanded ? sectionRooms : sectionRooms.slice(0, VISIBLE_LIMIT);
    const hiddenCount = sectionRooms.length - VISIBLE_LIMIT;

    return (
      <div style={{ marginBottom: isCollapsed ? '2px' : '4px' }}>
        <div
          className="sidebar-section-header"
          onClick={() => toggleSection(type)}
          style={{
            padding: "4px 20px",
            fontSize: "12px",
            fontWeight: "500",
            color: "#5f6368",
            textTransform: "uppercase",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
            userSelect: "none"
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
            <span>{title}</span>
          </div>
          {showAdd && (
            <Plus
              size={14}
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                if (type === 'direct') {
                  closeInline();
                  if (onNewChat) onNewChat('dm');
                } else {
                  if (isShowingInline) { closeInline(); } else { openInline(inlineKey); }
                }
              }}
            />
          )}
        </div>

        {!isCollapsed && (
          <>
            {isShowingInline && inlineKey !== 'dm' && renderInlineChannelInput()}
            {visibleRooms.map((room) => {
              const activeId = String(activeRoom?._id || activeRoom?.id || "");
              const roomId = String(room._id || room.id || "");
              const isActive = !!(activeId && roomId && activeId === roomId);
              return (
                <div
                  key={room._id || room.id}
                  className={`chat-room-item ${isActive ? "active" : ""}`}
                  onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    onSelectRoom(room, rect);
                  }}
                  style={{
                    padding: '4px 24px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    margin: '0 8px 0 4px',
                    borderRadius: '0 20px 20px 0',
                    transition: 'all 0.2s'
                  }}
                >
                  {room.type === "direct" ? (() => {
                    const other = room.members?.find(m => String(m.id || m._id) !== String(currentUserId));
                    const deleted = isDeletedMember(other);
                    return <RoomAvatar imgSrc={deleted ? null : other?.image} initial={deleted ? "D" : other?.first_name?.charAt(0)?.toUpperCase()} />;
                  })() : (
                    <RoomAvatar imgSrc={null} initial="#" />
                  )}
                  <div style={{ flex: 1, overflow: "hidden" }}>
                    <div style={{
                      fontSize: "13px",
                      fontWeight: "500",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      color: isActive ? "#1a73e8" : "#3c4043"
                    }}>
                      {getRoomName(room)}
                    </div>
                  </div>
                  {room.unreadCount > 0 && (
                    <div className="unread-badge">{room.unreadCount}</div>
                  )}
                </div>
              );
            })}

            {hiddenCount > 0 && !isExpanded && renderMoreRow(type, hiddenCount)}
            {isExpanded && sectionRooms.length > VISIBLE_LIMIT && renderLessRow(type)}
          </>
        )}
      </div>
    );
  };

  // ── Project Channels ────────────────────────────────────────────────────────
  const renderProjectChannels = () => {
    const isCollapsed = collapsedSections["project"];
    const projectRooms = filteredRooms.filter(r => r.type === "project");

    if (projectRooms.length === 0) return null;

    return (
      <div style={{ marginBottom: isCollapsed ? '2px' : '4px' }}>
        <div
          className="sidebar-section-header"
          onClick={() => toggleSection("project")}
          style={{
            padding: "4px 20px",
            fontSize: "12px",
            fontWeight: "500",
            color: "#5f6368",
            textTransform: "uppercase",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
            userSelect: "none"
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
            <span>Project Channels</span>
          </div>
        </div>

        {!isCollapsed && projectRooms.map((projRoom) => {
          const projKey = String(projRoom.id || projRoom._id);
          const isProjectCollapsed = collapsedProjects[projKey];
          const subChannels = rooms.filter(r => r.type === "channel" && r.parent_id != null && String(r.parent_id) === String(projRoom.id));
          const hasActiveChild = subChannels.some(
            sub => activeRoom?._id === sub._id || activeRoom?.id === sub.id
          );
          const isShowingSubInline = inlineInputType === 'subchannel' && inlineInputParentId === projKey;

          return (
            <div key={projKey}>
              <div
                onClick={() => toggleProject(projKey)}
                style={{
                  padding: '4px 16px 4px 20px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  margin: '0 8px 0 4px',
                  borderRadius: '0 20px 20px 0',
                  background: hasActiveChild ? '#e8f0fe' : 'transparent',
                  transition: 'background 0.15s',
                  userSelect: 'none'
                }}
                onMouseEnter={e => { if (!hasActiveChild) e.currentTarget.style.background = '#f1f3f4'; }}
                onMouseLeave={e => { if (!hasActiveChild) e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{ color: '#9e9e9e', flexShrink: 0 }}>
                  {isProjectCollapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
                </div>
                <div style={{ width: 24, height: 24, minWidth: 24, borderRadius: '5px', background: '#1a73e8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#fff' }}>
                    {(projRoom.name || "P").charAt(0).toUpperCase()}
                  </span>
                </div>
                <div style={{
                  flex: 1,
                  fontSize: '13px',
                  fontWeight: "500",
                  color: hasActiveChild ? '#1a73e8' : '#3c4043',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {projRoom.name}
                </div>
                <Plus
                  size={13}
                  color={isShowingSubInline ? "#0DDFAB" : "#9e9e9e"}
                  style={{ flexShrink: 0, cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isShowingSubInline) { closeInline(); } else { openInline('subchannel', projKey); }
                  }}
                />
              </div>

              {!isProjectCollapsed && (
                <>
                  {isShowingSubInline && (
                    <div style={{ paddingLeft: 48 }}>
                      {renderInlineChannelInput()}
                    </div>
                  )}
                  {subChannels.map((sub) => {
                    const isActive = activeRoom?._id === sub._id || activeRoom?.id === sub.id;
                    return (
                      <div
                        key={sub._id || sub.id}
                        className={`chat-room-item ${isActive ? "active" : ""}`}
                        onClick={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          onSelectRoom(sub, rect);
                        }}
                        style={{
                          padding: '4px 16px 4px 0',
                          paddingLeft: 52,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          margin: '1px 8px 1px 4px',
                          borderRadius: '0 16px 16px 0',
                          transition: 'all 0.2s',
                        }}
                      >
                        <div style={{ width: 22, height: 22, minWidth: 22, borderRadius: '5px', background: '#0DDFAB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>#</span>
                        </div>
                        <div style={{ flex: 1, overflow: 'hidden' }}>
                          <div style={{
                            fontSize: '12px',
                            fontWeight: "500",
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            color: isActive ? '#1a73e8' : '#5f6368'
                          }}>
                            {sub.name}
                          </div>
                        </div>
                        {sub.unreadCount > 0 && (
                          <div className="unread-badge">{sub.unreadCount}</div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="chat-sidebar">
      <div style={{ padding: "20px", borderBottom: "1px solid #f0f0f0", flexShrink: 0 }}>
        <h4 style={{ margin: 0, fontSize: "18px", fontWeight: "500", color: "#202124" }}>Chat</h4>
        <div style={{
          marginTop: "10px",
          display: "flex",
          alignItems: "center",
          background: "#f1f3f4",
          borderRadius: "8px",
          padding: "8px 12px"
        }}>
          <Search size={16} color="#5f6368" />
          <input
            type="search"
            autoComplete="off"
            data-form-type="other"
            placeholder="Search channels & DMs"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              background: "none",
              border: "none",
              marginLeft: "8px",
              fontSize: "14px",
              outline: "none",
              width: "100%"
            }}
          />
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        {renderSection("Inbox", "direct", true)}
        {renderProjectChannels()}
        {renderSection("Channels", "channel", true)}
      </div>

      <div style={{ padding: "10px 20px", flexShrink: 0 }}>
        <button
          onClick={() => onNewChat && onNewChat('dm')}
          style={{
            width: "100%",
            padding: "10px",
            background: "#1a73e8",
            color: "white",
            border: "none",
            borderRadius: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            cursor: "pointer",
            fontWeight: "500",
            boxShadow: "0 1px 3px rgba(60,64,67,0.3)"
          }}
        >
          <Plus size={18} />
          <span>New Chat</span>
        </button>
      </div>
    </div>
  );
};

export default RoomList;
